import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Button,
  Card,
  Checkbox,
  Empty,
  Flex,
  Form,
  Input,
  Modal,
  Space,
  Spin,
  Table,
  Typography,
} from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import {
  getCompanyLists,
  createCompanyList,
  updateCompanyList,
  deleteCompanyList,
  getCompanyListCompanies,
} from '../api';
import './workspaceInformation/wiPhase1.css';

const { Title, Text, Paragraph } = Typography;

const ManageCompanyLists = () => {
  const navigate = useNavigate();
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editPopup, setEditPopup] = useState(null);
  const [editForm, setEditForm] = useState({
    name: '',
    description: '',
    id: '',
    sampleCompanies: [],
  });
  const [originalForm, setOriginalForm] = useState({
    name: '',
    description: '',
    id: '',
    sampleCompanies: [],
  });
  const [allCompanies, setAllCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [createPopup, setCreatePopup] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', description: '' });
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    loadLists();
  }, []);

  const loadLists = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getCompanyLists();
      setLists(response.data.data || []);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to load lists');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (list) => {
    try {
      setLoadingCompanies(true);
      const companiesResponse = await getCompanyListCompanies(list.id);
      const companies = companiesResponse.data.data || [];
      setAllCompanies(companies);

      const sampleCompaniesMap = new Map();
      (list.sample_companies || []).forEach((sc) => {
        const name = sc.company_name || sc[0] || '';
        const logo = sc.company_logo || sc[1] || '';
        if (name) {
          sampleCompaniesMap.set(name.toLowerCase(), { name, logo });
        }
      });

      const sampleCompanyIds = [];
      companies.forEach((company) => {
        const companyName = (company.name || company.company_name || '').toLowerCase();
        const existingSample = sampleCompaniesMap.get(companyName);
        if (existingSample) {
          if (existingSample.logo && !company.company_logo && !company.logo) {
            company._preservedLogo = existingSample.logo;
          }
          sampleCompanyIds.push(company.id || company.company_id);
        }
      });

      const formData = {
        name: list.name || '',
        description: list.description || '',
        id: list.id || '',
        sampleCompanies: sampleCompanyIds,
      };

      setEditForm(formData);
      setOriginalForm(JSON.parse(JSON.stringify(formData)));
      setEditPopup(list);
    } catch (err) {
      console.error('Error loading companies:', err);
      setError('Failed to load companies for editing');
    } finally {
      setLoadingCompanies(false);
    }
  };

  const handleSaveEdit = async () => {
    try {
      const hasChanges =
        editForm.name !== originalForm.name ||
        editForm.description !== originalForm.description ||
        JSON.stringify([...editForm.sampleCompanies].sort()) !==
          JSON.stringify([...originalForm.sampleCompanies].sort());

      if (!hasChanges) {
        setEditPopup(null);
        setEditForm({ name: '', description: '', id: '', sampleCompanies: [] });
        setOriginalForm({ name: '', description: '', id: '', sampleCompanies: [] });
        return;
      }

      setLoading(true);

      const sampleCompaniesData = editForm.sampleCompanies.map((companyId) => {
        const company = allCompanies.find((c) => (c.id || c.company_id) === companyId);
        const logo = company?._preservedLogo || company?.company_logo || company?.logo || '';
        return {
          company_name: company?.name || company?.company_name || 'Unknown',
          company_logo: logo,
        };
      });

      const updateData = {
        name: editForm.name,
        description: editForm.description,
        sample_companies: sampleCompaniesData,
      };

      await updateCompanyList(editPopup.id, updateData);
      setEditPopup(null);
      setEditForm({ name: '', description: '', id: '', sampleCompanies: [] });
      setOriginalForm({ name: '', description: '', id: '', sampleCompanies: [] });
      await loadLists();
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to update list');
    } finally {
      setLoading(false);
    }
  };

  const handleSampleCompanyToggle = (companyId) => {
    const current = editForm.sampleCompanies || [];
    if (current.includes(companyId)) {
      setEditForm({ ...editForm, sampleCompanies: current.filter((id) => id !== companyId) });
    } else if (current.length < 3) {
      setEditForm({ ...editForm, sampleCompanies: [...current, companyId] });
    }
  };

  const handleCreateList = async () => {
    try {
      if (!createForm.name || !createForm.name.trim()) {
        setError('List name is required');
        return;
      }

      setLoading(true);
      setError(null);

      await createCompanyList({
        name: createForm.name.trim(),
        description: createForm.description.trim() || '',
      });

      setCreatePopup(false);
      setCreateForm({ name: '', description: '' });
      await loadLists();
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to create list');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteList = async () => {
    if (!deleteConfirm) return;

    try {
      setLoading(true);
      setError(null);

      await deleteCompanyList(deleteConfirm.id);
      setDeleteConfirm(null);
      await loadLists();
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to delete list');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
        width: 140,
        render: (id) => <Text code>{id}</Text>,
      },
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        render: (name) => name || '—',
      },
      {
        title: 'Description',
        dataIndex: 'description',
        key: 'description',
        render: (d) => d || '—',
      },
      {
        title: 'Sample Companies',
        key: 'samples',
        render: (_, list) => {
          if (!list.sample_companies || list.sample_companies.length === 0) {
            return <Text type="secondary">None</Text>;
          }
          return (
            <Space wrap size={8}>
              {list.sample_companies.map((sc, idx) => {
                const companyName = sc.company_name || sc[0] || 'Unknown';
                const companyLogo = sc.company_logo || sc[1] || '';
                return (
                  <Space key={idx} size={4}>
                    {companyLogo ? (
                      <Avatar src={companyLogo} size={20} shape="square" alt={companyName} />
                    ) : null}
                    <Text style={{ fontSize: 13 }}>{companyName}</Text>
                  </Space>
                );
              })}
            </Space>
          );
        },
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 140,
        align: 'center',
        render: (_, list) => (
          <Space>
            <Button
              icon={<EditOutlined />}
              onClick={() => handleEdit(list)}
              title="Edit List"
            />
            <Button
              icon={<EyeOutlined />}
              onClick={() => navigate(`/manage-company-lists/${list.id}`)}
              title="View Companies"
            />
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={() => setDeleteConfirm(list)}
              title="Delete List"
            />
          </Space>
        ),
      },
    ],
    [navigate]
  );

  return (
    <div className="wi-page">
      <div className="wi-panel" style={{ maxWidth: 1100 }}>
        <Card bordered={false} className="wi-search-card">
          <Flex justify="space-between" align="flex-start" gap={16} wrap="wrap">
            <div>
              <Title level={3} style={{ margin: 0 }}>
                Manage Company Lists
              </Title>
              <Paragraph type="secondary" style={{ marginBottom: 0, marginTop: 4 }}>
                Create, edit, and manage curated company lists
              </Paragraph>
            </div>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreatePopup(true)}>
              Create New List
            </Button>
          </Flex>
        </Card>

        {error ? (
          <Alert
            type="error"
            showIcon
            message={error}
            style={{ marginTop: 16 }}
            closable
            onClose={() => setError(null)}
          />
        ) : null}

        <Card style={{ marginTop: 16 }} className="wi-details-card">
          {loading && lists.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 48 }}>
              <Spin size="large" tip="Loading lists…" />
            </div>
          ) : lists.length === 0 ? (
            <Empty description="No company lists yet" />
          ) : (
            <Table
              size="middle"
              rowKey={(row) => row.id}
              dataSource={lists}
              columns={columns}
              pagination={false}
              scroll={{ x: true }}
              loading={loading}
            />
          )}
        </Card>
      </div>

      <Modal
        open={!!editPopup}
        title="Edit List"
        onCancel={() => {
          setEditPopup(null);
          setEditForm({ name: '', description: '', id: '', sampleCompanies: [] });
          setOriginalForm({ name: '', description: '', id: '', sampleCompanies: [] });
        }}
        width={600}
        footer={
          <Space>
            <Button
              onClick={() => {
                setEditPopup(null);
                setEditForm({ name: '', description: '', id: '', sampleCompanies: [] });
                setOriginalForm({ name: '', description: '', id: '', sampleCompanies: [] });
              }}
            >
              Cancel
            </Button>
            <Button type="primary" onClick={handleSaveEdit} loading={loading}>
              Save
            </Button>
          </Space>
        }
      >
        <Form layout="vertical">
          <Form.Item label="Name">
            <Input
              value={editForm.name}
              onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
            />
          </Form.Item>
          <Form.Item label="Description">
            <Input.TextArea
              value={editForm.description}
              onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
              rows={3}
            />
          </Form.Item>
          <Form.Item label="ID (read-only)">
            <Input value={editForm.id} disabled readOnly />
          </Form.Item>
          <Form.Item label="Sample Companies (select up to 3)">
            {loadingCompanies ? (
              <Spin tip="Loading companies…" />
            ) : (
              <div
                style={{
                  maxHeight: 200,
                  overflow: 'auto',
                  border: '1px solid var(--ant-color-border, #d9d9d9)',
                  borderRadius: 6,
                  padding: 8,
                }}
              >
                {allCompanies.map((company) => {
                  const companyLogo =
                    company._preservedLogo || company.company_logo || company.logo || '';
                  const companyName =
                    company.name || company.company_name || company.linkedin_url || 'Unknown';
                  const companyId = company.id || company.company_id;
                  const isSelected = editForm.sampleCompanies.includes(companyId);
                  return (
                    <div key={companyId} style={{ padding: '6px 4px' }}>
                      <Checkbox
                        checked={isSelected}
                        disabled={!isSelected && editForm.sampleCompanies.length >= 3}
                        onChange={() => handleSampleCompanyToggle(companyId)}
                      >
                        <Space size={8}>
                          {companyLogo ? (
                            <Avatar src={companyLogo} size={24} shape="square" alt={companyName} />
                          ) : null}
                          <span>{companyName}</span>
                        </Space>
                      </Checkbox>
                    </div>
                  );
                })}
              </div>
            )}
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={createPopup}
        title="Create New List"
        onCancel={() => {
          setCreatePopup(false);
          setCreateForm({ name: '', description: '' });
          setError(null);
        }}
        footer={
          <Space>
            <Button
              onClick={() => {
                setCreatePopup(false);
                setCreateForm({ name: '', description: '' });
                setError(null);
              }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={handleCreateList}
              loading={loading}
              disabled={!createForm.name.trim()}
            >
              Create List
            </Button>
          </Space>
        }
      >
        <Form layout="vertical">
          <Form.Item
            label="Name"
            required
            extra='List ID will be generated automatically (e.g., "My List" → "my_list")'
          >
            <Input
              value={createForm.name}
              onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
              placeholder="Enter list name"
            />
          </Form.Item>
          <Form.Item label="Description">
            <Input.TextArea
              value={createForm.description}
              onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
              placeholder="Enter list description (optional)"
              rows={3}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={!!deleteConfirm}
        title="Delete List"
        onCancel={() => setDeleteConfirm(null)}
        okText="Delete"
        okButtonProps={{ danger: true, loading }}
        onOk={handleDeleteList}
      >
        <Paragraph>
          Are you sure you want to delete the list{' '}
          <Text strong>&quot;{deleteConfirm?.name}&quot;</Text> (ID: {deleteConfirm?.id})?
        </Paragraph>
        <Paragraph type="secondary">
          This will remove the list and remove this label from all associated companies. This
          action cannot be undone.
        </Paragraph>
      </Modal>
    </div>
  );
};

export default ManageCompanyLists;
